import React, { useState, useEffect } from "react";
import Select from "../components/Select";
import { Field } from "formik";
import classNames from "classnames";

// Se vuoi digitare le props, puoi creare un'interfaccia:
interface OrderBy {
  column: string;
  mode: "ASC" | "DESC";
}

interface BackorderFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: string;
  fromDelivery: string | null;
  toDelivery: string | null;
  setFromDelivery: React.Dispatch<React.SetStateAction<string | null>>;
  setToDelivery: React.Dispatch<React.SetStateAction<string | null>>;
  ritiroCliente: string;
  setRitiroCliente: React.Dispatch<React.SetStateAction<string>>;
  tipoConsegna: string;
  setTipoConsegna: React.Dispatch<React.SetStateAction<string>>;
  quantita: string;
  setQuantita: React.Dispatch<React.SetStateAction<string>>;
  codiceArticolo: string;
  setCodiceArticolo: React.Dispatch<React.SetStateAction<string>>;
  applyFilters: () => void;
  clearFiltri: (e: any) => void;
  orderBy: OrderBy[];
  handleOrderByChange: (column: string) => void;
  clearOrderBy: () => void;
  visibleColumns: string[];
}

const BackorderFiltersModal: React.FC<BackorderFiltersModalProps> = ({
  isOpen,
  onClose,
  theme,
  fromDelivery,
  toDelivery,
  setFromDelivery,
  setToDelivery,
  ritiroCliente,
  setRitiroCliente,
  tipoConsegna,
  setTipoConsegna,
  quantita,
  setQuantita,
  codiceArticolo,
  setCodiceArticolo,
  applyFilters,
  clearFiltri,
  orderBy,
  handleOrderByChange,
  clearOrderBy,
  visibleColumns,
}) => {
  // Stati locali per i filtri già esistenti
  const [localRitiroCliente, setLocalRitiroCliente] = useState(ritiroCliente);
  const [localTipoConsegna, setLocalTipoConsegna] = useState(tipoConsegna);
  const [localOrderBy, setLocalOrderBy] = useState<OrderBy[]>(orderBy);

  const [localQuantita, setLocalQuantita] = useState<string>(quantita);
  const [localCodiceArticolo, setLocalCodiceArticolo] = useState<string>(codiceArticolo);

  useEffect(() => {
    if (isOpen) {
      setLocalRitiroCliente(ritiroCliente);
      setLocalTipoConsegna(tipoConsegna);
      setLocalOrderBy(orderBy);
      setLocalQuantita(quantita);
      setLocalCodiceArticolo(codiceArticolo);
    }
  }, [isOpen, ritiroCliente, tipoConsegna, orderBy, quantita, codiceArticolo]);

  if (!isOpen) return null;

  // Quando l'utente clicca "Applica Filtri",
  // trasferiamo i filtri locali nel padre
  const handleApplyFilters = () => {
    setRitiroCliente(localRitiroCliente);
    setTipoConsegna(localTipoConsegna);
    setQuantita(localQuantita);
    setCodiceArticolo(localCodiceArticolo);

    // Se l'ordinamento locale è cambiato, lo applichiamo
    if (JSON.stringify(localOrderBy) !== JSON.stringify(orderBy)) {
      // Esempio semplice: applichiamo i cambi su handleOrderByChange
      localOrderBy.forEach((ord) => {
        handleOrderByChange(ord.column);
      });
    }

    onClose();
  };

  // Gestione dell’ordinamento in locale
  const handleLocalOrderByChange = (column: string) => {
    if (localOrderBy.length > 0 && localOrderBy[0].column === column) {
      setLocalOrderBy([
        {
          column,
          mode: localOrderBy[0].mode === "ASC" ? "DESC" : "ASC",
        },
      ]);
    } else {
      setLocalOrderBy([{ column, mode: "ASC" }]);
    }
  };

  // Reset locale di tutti i filtri
  const handleClearAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setLocalRitiroCliente("Tutti");
    setLocalTipoConsegna("Tutti");
    setLocalOrderBy([]);
    setLocalQuantita("Tutti");
    setLocalCodiceArticolo("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        {/* Header della modal */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Filtri
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {/* Filtro CODICE ARTICOLO */}
        <div className="mb-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Codice Articolo
          </div>
          <input
            type="text"
            value={localCodiceArticolo}
            onChange={(e) => setLocalCodiceArticolo(e.target.value.toUpperCase())}
            placeholder="Es. ART001"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filtro QUANTITA */}
        <div className="mb-4">
          <div className="py-3 flex items-center text-neutral-600 dark:text-white">
            <span>Quantità</span>
          </div>
          <div className="flex">
            {/* Assegnata */}
            <label
              htmlFor="quantita_assegnata"
              className={`min-h-c flex justify-center p-2 w-full border border-r-0 border-gray-200 text-sm
                          focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white
                          ${localQuantita === "Si" ? "bg-blue-600 text-white" : ""}`}
            >
              <input
                type="radio"
                id="quantita_assegnata"
                name="quantita"
                className="hidden"
                onChange={() => setLocalQuantita("Si")}
                value="Si"
                checked={localQuantita === "Si"}
              />
              Assegnata
            </label>

            {/* Non Assegnata */}
            <label
              htmlFor="quantita_non_assegnata"
              className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm
                          focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white
                          ${localQuantita === "No" ? "bg-blue-600 text-white" : ""}`}
            >
              <input
                type="radio"
                id="quantita_non_assegnata"
                name="quantita"
                className="hidden"
                onChange={() => setLocalQuantita("No")}
                value="No"
                checked={localQuantita === "No"}
              />
              Non assegnata
            </label>

            {/* Tutte */}
            <label
              htmlFor="quantita_tutte"
              className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm
                          focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white
                          ${localQuantita === "Tutti" ? "bg-blue-600 text-white" : ""}`}
            >
              <input
                type="radio"
                id="quantita_tutte"
                name="quantita"
                className="hidden"
                onChange={() => setLocalQuantita("Tutti")}
                value="Tutti"
                checked={localQuantita === "Tutti"}
              />
              Tutte
            </label>
          </div>
        </div>

        {/* Filtro RITIRO CLIENTE */}
        <div className="mb-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ritiro Cliente
          </div>
          <div className="flex divide-x divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600 rounded">
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localRitiroCliente === "Si"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="ritiro_cliente"
                className="hidden"
                onChange={() => setLocalRitiroCliente("Si")}
                checked={localRitiroCliente === "Si"}
              />
              Sì
            </label>
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localRitiroCliente === "No"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="ritiro_cliente"
                className="hidden"
                onChange={() => setLocalRitiroCliente("No")}
                checked={localRitiroCliente === "No"}
              />
              No
            </label>
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localRitiroCliente === "Tutti"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="ritiro_cliente"
                className="hidden"
                onChange={() => setLocalRitiroCliente("Tutti")}
                checked={localRitiroCliente === "Tutti"}
              />
              Tutti
            </label>
          </div>
        </div>

        {/* Filtro TIPOLOGIA CONSEGNA */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tipologia Consegna
          </div>
          <div className="flex divide-x divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600 rounded">
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localTipoConsegna === "Totale"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="tipo_consegna"
                className="hidden"
                onChange={() => setLocalTipoConsegna("Totale")}
                checked={localTipoConsegna === "Totale"}
              />
              Totale
            </label>
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localTipoConsegna === "Parziale"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="tipo_consegna"
                className="hidden"
                onChange={() => setLocalTipoConsegna("Parziale")}
                checked={localTipoConsegna === "Parziale"}
              />
              Parziale
            </label>
            <label
              className={classNames(
                "flex-1 text-center py-2 cursor-pointer transition-colors",
                localTipoConsegna === "Tutti"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              )}
            >
              <input
                type="radio"
                name="tipo_consegna"
                className="hidden"
                onChange={() => setLocalTipoConsegna("Tutti")}
                checked={localTipoConsegna === "Tutti"}
              />
              Tutte
            </label>
          </div>
        </div>

        {/* ORDINAMENTO */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ordinamento
          </div>
          <div className="space-y-2">
            {localOrderBy.map((order) => (
              <div
                key={order.column}
                className="flex justify-between items-center p-2 border rounded"
              >
                <span>{order.column}</span>
                <div className="flex gap-2">
                  <span
                    onClick={() => handleLocalOrderByChange(order.column)}
                    className={classNames(
                      "material-symbols-outlined cursor-pointer text-xl",
                      {
                        "text-neutral-300 dark:text-natural-600":
                          order.mode !== "DESC",
                        "text-blue-500 dark:text-blue-400": order.mode === "DESC",
                      }
                    )}
                  >
                    arrow_upward
                  </span>
                  <span
                    onClick={() => handleLocalOrderByChange(order.column)}
                    className={classNames(
                      "material-symbols-outlined cursor-pointer text-xl",
                      {
                        "text-neutral-300 dark:text-natural-600":
                          order.mode !== "ASC",
                        "text-blue-500 dark:text-blue-400": order.mode === "ASC",
                      }
                    )}
                  >
                    arrow_downward
                  </span>
                </div>
              </div>
            ))}

            <select
              onChange={(e) => handleLocalOrderByChange(e.target.value)}
              value=""
              className="w-full p-2 border rounded bg-white dark:bg-gray-700"
            >
              <option value="">+ Ordinamento</option>
              {visibleColumns.map((column) => (
                <option key={column} value={column}>
                  {column}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bottoni a fondo modal */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleClearAll}
            className="px-4 py-2 text-sm border border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
          >
            Rimuovi tutti
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            Applica Filtri
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackorderFiltersModal;
